import { Divider, Grid } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import InfoBlock from '../components/InfoBlock'
import { SimpleCardBlock } from '../components/SimpleCardBlock'
import WarrantOrigin from './WarrantOrigin'
import { AdjustPriceBlock, ExternalCommentBlock, warrantOtherInfoHasContent } from './WarrantOtherInfo'
import WarrantOutcome, { warrantOutcomeHasContent } from './WarrantOutcome'
import WarrantTradingStats from './WarrantTradingStats'
import WarrantTerms from './WarrantTerms'
import { ExtendedWarrantSourceType, SourcesTable } from '../../components/entities/SourcesTable'
import UnderwritersAndGuarantorsTable from '../components/UnderwritersAndGuarantorsTable'
import { SourceEntity } from 'types/SourceEntity'
import { WarrantSummary } from './WarrantSummary'
import { WarrantListItem } from '@finance-lab/utils/types'

type ExtendedSourceEntity = Omit<SourceEntity, 'sourceType'> & {
    sourceType: ExtendedWarrantSourceType | 'PublicationWarrant'
}

function WarrantDetailsView({ warrant }: { warrant: WarrantListItem }) {
    const { t } = useTranslation()

    const publications =
        warrant.origins?.reduce((acc, curr) => {
            if (!curr.sources) {
                return acc
            }

            const publicationSources = curr.sources.filter((source) => source.sourceType === 'Publication')

            for (const publicationSource of publicationSources) {
                const alreadyExists = acc.find((source) => source.sourceUrl === publicationSource.sourceUrl)

                if (!alreadyExists) {
                    acc.push({ ...publicationSource, sourceType: 'PublicationWarrant' })
                }
            }

            return acc
        }, [] as Array<ExtendedSourceEntity>) || []

    const sources: ExtendedSourceEntity[] = [...warrant.sources, ...publications]

    return (
        <Grid container spacing={6}>
            {!!warrant.cancelled && (
                <Grid size={12}>
                    <SimpleCardBlock>{t('pages.warrants.cancelledNotice')}</SimpleCardBlock>
                </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
                <WarrantSummary isDetailsPage isLoading={false} warrant={warrant} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                {/* Optionens ursprung? */}
                <InfoBlock title={t('pages.warrants.sections.origin')}>
                    <WarrantOrigin warrant={warrant} />
                </InfoBlock>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <InfoBlock title={t('pages.warrants.sections.terms')}>
                    <WarrantTerms warrant={warrant} />
                </InfoBlock>
            </Grid>
            {(warrant.investors.length && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <InfoBlock title={t('pages.warrants.sections.subscribersAndGuarantors')}>
                        <UnderwritersAndGuarantorsTable
                            currency={warrant.currency}
                            fees={warrant.fees || []}
                            hideTotalColumns
                            investors={warrant.investors}
                            isUnit={false}
                            totalOfferingSize={0}
                        />
                    </InfoBlock>
                </Grid>
            )) ||
                null}
            {warrantOutcomeHasContent(warrant) && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <InfoBlock title={t('pages.warrants.sections.outcome')}>
                        <WarrantOutcome warrant={warrant} />
                    </InfoBlock>
                </Grid>
            )}
            {!warrant.unlisted && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <InfoBlock title={t('pages.warrants.sections.tradingStats')}>
                        <WarrantTradingStats warrant={warrant} />
                    </InfoBlock>
                </Grid>
            )}
            {warrantOtherInfoHasContent(warrant, sources) && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <InfoBlock title={t('pages.warrants.sections.other')}>
                        {[
                            warrant.externalComment && (
                                <ExternalCommentBlock externalComment={warrant.externalComment} />
                            ),
                            warrant.issuesAdjustPrice && <AdjustPriceBlock />,
                            sources.length > 0 && <SourcesTable companyId={warrant.companyId} sources={sources} />,
                        ]
                            .filter((el) => !!el)
                            .map((el, index) => (
                                <>
                                    {!!index && <Divider sx={{ my: 3 }} />}
                                    {el}
                                </>
                            ))}
                    </InfoBlock>
                </Grid>
            )}
        </Grid>
    )
}

export default WarrantDetailsView
